import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, ThemeProvider, Toolbar, createTheme, useTheme } from '@mui/material';
import Sidebar, { DRAWER_WIDTH } from './Sidebar';
import TopBar from './TopBar';
import SearchDialog from '../common/SearchDialog';
import { NotifyProvider } from '../../hooks/NotifyContext';
import { useEasterEgg } from '../../hooks/EasterEggContext';
import { useKonamiCode } from '../../hooks/useKonamiCode';
import { useNotification } from '../../hooks/NotificationContext';
import { DEV_MODE_ACCENT, KONAMI_TOAST } from '../../utils/easterEggs';
import { SaisonProvider } from '../../hooks/SaisonContext';

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const { devMode, triggerKonami } = useEasterEgg();
  const { showInfo } = useNotification();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useKonamiCode(() => {
    triggerKonami();
    showInfo(KONAMI_TOAST);
  });

  const devTheme = useMemo(
    () => createTheme(theme, { palette: { primary: { main: DEV_MODE_ACCENT } } }),
    [theme],
  );

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const handleDrawerToggle = useCallback(() => setMobileOpen((prev) => !prev), []);
  const handleDrawerClose = useCallback(() => setMobileOpen(false), []);

  /* Global keyboard shortcut: Ctrl+K or Cmd+K */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const layout = (
    <SaisonProvider>
      <NotifyProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Sidebar mobileOpen={mobileOpen} onMobileClose={handleDrawerClose} />
        <TopBar onOpenSearch={openSearch} onDrawerToggle={handleDrawerToggle} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
            bgcolor: 'background.default',
            minWidth: 0,
          }}
        >
          <Toolbar />
          <Outlet />
        </Box>
        <SearchDialog open={searchOpen} onClose={closeSearch} />
      </Box>
      </NotifyProvider>
    </SaisonProvider>
  );

  if (!devMode) return layout;
  return <ThemeProvider theme={devTheme}>{layout}</ThemeProvider>;
};

export default MainLayout;
